import { LOGIN_SITE } from '~/lib/constants/constants'
import type { PaypayUnavailableCause } from '~/lib/graphql/graphql'
import type {
  ReserveInputPlan,
  ReserveInputRestaurant,
  ReserveInputViewer,
} from '../stateMachine/state'

export function disabledGivenPointsUsageUseInstantly(
  isMemberOrPreMember: boolean,
  restaurant?: ReserveInputRestaurant,
  plan?: ReserveInputPlan,
  paymentAmountBeforeUseInstantly?: number,
  // 即時利用時のポイント数
  givenPointsUseInstantly?: number
) {
  return (
    !isMemberOrPreMember ||
    restaurant === undefined ||
    plan === undefined ||
    paymentAmountBeforeUseInstantly === undefined ||
    givenPointsUseInstantly === undefined ||
    !restaurant.pointAvailable ||
    plan.isSeatOnly ||
    paymentAmountBeforeUseInstantly < givenPointsUseInstantly
  )
}

if (import.meta.vitest) {
  const { describe, expect, test } = import.meta.vitest
  describe('disabledGivenPointsUsageUseInstantly', () => {
    test.each`
      description                                                 | isMemberOrPreMember | pointAvailable | isSeatOnly   | paymentAmount | givenPoints  | expected
      ${'会員でない'}                                             | ${false}            | ${true}        | ${true}      | ${10000}      | ${0}         | ${true}
      ${'ロード前'}                                               | ${true}             | ${undefined}   | ${undefined} | ${undefined}  | ${undefined} | ${true}
      ${'席のみなので即時利用できない'}                           | ${true}             | ${true}        | ${true}      | ${10000}      | ${1000}      | ${true}
      ${'店舗が予約時割引できない'}                               | ${true}             | ${false}       | ${false}     | ${10000}      | ${1000}      | ${true}
      ${'店舗が予約時割引できずかつ席のみなので即時利用できない'} | ${true}             | ${false}       | ${true}      | ${10000}      | ${1000}      | ${true}
      ${'即時利用できる'}                                         | ${true}             | ${true}        | ${false}     | ${10000}      | ${1000}      | ${false}
      ${'支払金額と付与ポイントが同じなので即時利用できる'}       | ${true}             | ${true}        | ${false}     | ${1000}       | ${1000}      | ${false}
      ${'支払金額より付与ポイントが多いので即時利用できない'}     | ${true}             | ${true}        | ${false}     | ${250}        | ${1000}      | ${true}
    `(
      '$description',
      ({
        isMemberOrPreMember,
        pointAvailable,
        isSeatOnly,
        paymentAmount,
        givenPoints,
        expected,
      }) => {
        expect(
          disabledGivenPointsUsageUseInstantly(
            isMemberOrPreMember,
            pointAvailable !== undefined
              ? ({ pointAvailable } as ReserveInputRestaurant)
              : undefined,
            isSeatOnly !== undefined ? ({ isSeatOnly } as ReserveInputPlan) : undefined,
            paymentAmount,
            givenPoints
          )
        ).toBe(expected)
      }
    )
  })
}

export function disabledGivenPointsTypePayPay(cause?: PaypayUnavailableCause) {
  // 仕様：https://www.figma.com/file/Xizn2jb2WlVtzaAoZf0maY/Master?type=design&node-id=5632-36610&mode=design&t=ICF289sjTzOXyYj7-0
  return cause === 'YAHOO_ID_NOT_CONNECTED'
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('disabledGivenPointsTypePaypay', () => {
    expect(disabledGivenPointsTypePayPay('PHONE_NUMBER_UNVERIFIED')).toBe(false)
    expect(disabledGivenPointsTypePayPay('YAHOO_ID_NOT_CONNECTED')).toBe(true)
  })
}

export const disabledGivenPointsUsageSave =
  import.meta.env.VITE_MODE === 'ppg'
    ? ppgDisabledGivenPointsUsageSave
    : ikyuDisabledGivenPointsUsageSave

function ikyuDisabledGivenPointsUsageSave(
  cause?: PaypayUnavailableCause,
  viewer?: ReserveInputViewer
) {
  // 仕様：https://www.figma.com/file/Xizn2jb2WlVtzaAoZf0maY/Master?type=design&node-id=5632-36638&mode=design&t=ICF289sjTzOXyYj7-0
  return cause === 'INVALID_YAHOO_ID_TOKEN' && viewer?.loginSite === LOGIN_SITE.Yahoo
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('ikyuDisabledGivenPointsUsageSave', () => {
    expect(
      ikyuDisabledGivenPointsUsageSave('INVALID_YAHOO_ID_TOKEN', {
        loginSite: LOGIN_SITE.Yahoo,
      } as ReserveInputViewer)
    ).toBe(true)
    expect(
      ikyuDisabledGivenPointsUsageSave('PAYPAY_NOT_CONNECTED', {
        loginSite: LOGIN_SITE.Yahoo,
      } as ReserveInputViewer)
    ).toBe(false)
    expect(
      ikyuDisabledGivenPointsUsageSave('PAYPAY_NOT_CONNECTED', {
        loginSite: LOGIN_SITE.Ikyu,
      } as ReserveInputViewer)
    ).toBe(false)
  })
}

function ppgDisabledGivenPointsUsageSave(
  cause?: PaypayUnavailableCause,
  _viewer?: ReserveInputViewer
) {
  return cause === 'INVALID_YAHOO_ID_TOKEN'
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('ppgDisabledGivenPointsUsageSave', () => {
    expect(ppgDisabledGivenPointsUsageSave('INVALID_YAHOO_ID_TOKEN')).toBe(true)
    expect(ppgDisabledGivenPointsUsageSave('PAYPAY_NOT_CONNECTED')).toBe(false)
  })
}

export function disabledOnlineCard(
  plan: ReserveInputPlan | undefined,
  restaurant: ReserveInputRestaurant | undefined
) {
  return (
    !restaurant || !plan || !restaurant.cardPaymentAvailable || plan.paymentType === 'ONSITE_ONLY'
  )
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('disabledOnlineCard', async () => {
    expect(
      disabledOnlineCard(
        { paymentType: 'ONSITE_OR_ONLINE' } as ReserveInputPlan,
        { cardPaymentAvailable: false } as ReserveInputRestaurant
      )
    ).toBe(true)
    expect(
      disabledOnlineCard(
        { paymentType: 'ONSITE_ONLY' } as ReserveInputPlan,
        { cardPaymentAvailable: true } as ReserveInputRestaurant
      )
    ).toBe(true)
    expect(
      disabledOnlineCard(
        { paymentType: 'ONLINE_ONLY' } as ReserveInputPlan,
        { cardPaymentAvailable: true } as ReserveInputRestaurant
      )
    ).toBe(false)
  })
}

export function disabledOnsite(plan: ReserveInputPlan | undefined) {
  return !plan || plan.paymentType === 'ONLINE_ONLY'
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('disabledOnsite', async () => {
    expect(disabledOnsite({ paymentType: 'ONSITE_OR_ONLINE' } as ReserveInputPlan)).toBe(false)
    expect(disabledOnsite({ paymentType: 'ONLINE_ONLY' } as ReserveInputPlan)).toBe(true)
    expect(disabledOnsite({ paymentType: 'ONSITE_ONLY' } as ReserveInputPlan)).toBe(false)
  })
}

export type SelectedCardStatus =
  | { type: 'registered'; cardId: string }
  | { type: 'new' }
  | { type: 'deleted' }

export type CardErrors = {
  cardNumberError?: string
  cardHolderError?: string
  securityCodeError?: string
  cardExpirationError?: string
}

export function hasCardError(errors: CardErrors) {
  return Object.values(errors).some((error) => error !== undefined && error !== '')
}

/** カードの入力状態を見て、カード決定の disabled 状態を返す */
export function submitCardDisabled(
  isInputCompleted: boolean,
  errors: CardErrors,
  selected?: SelectedCardStatus
) {
  if (selected === undefined) {
    return true
  }
  if (selected?.type === 'new') {
    return hasCardError(errors) || !isInputCompleted
  }
  return false
}
if (import.meta.vitest) {
  const { expect, it } = import.meta.vitest
  it('submitCardDisabled', () => {
    const errors = {
      cardNumberError: 'test',
      cardHolderError: 'test',
      securityCodeError: 'test',
      cardExpirationError: 'test',
    }
    expect(submitCardDisabled(true, errors, { type: 'new' })).toBe(true)
    expect(submitCardDisabled(false, {}, { type: 'registered', cardId: '1' })).toBe(false)
    expect(submitCardDisabled(true, {}, { type: 'new' })).toBe(false)
    expect(submitCardDisabled(false, {}, undefined)).toBe(true)
  })
}
